import React from "react";
import { Button } from "../ui/button";
import { useWorkflowStore } from "../../store/useWorkflowStore";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Checkbox } from "../ui/checkbox";
import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";
import OntologyRecordsTable from "./OntologyRecordsTable";
import IntegrationParameterPreview from "./IntegrationParameterPreview";
import ChecklistValidationReportPanel from "./ChecklistValidationReportPanel";
import { ChecklistValidationReport } from "../../lib/api";

/**
 * WorkflowResults component
 *
 * Displays the results of the checklist import and allows confirming the migration
 */
interface WorkflowResultsProps {
  onBack: () => void;
  onConfirm: () => void;
  workflowType: 'process' | 'ontology';
  /** Per-checklist reports from an aborted multi-checklist migration (server-side validation). */
  validationReports?: ChecklistValidationReport[] | null;
  /** Extra context for the abort panel (e.g. mixed linked/standard runs). */
  validationContextNote?: string;
}

const WorkflowResults: React.FC<WorkflowResultsProps> = ({ onBack, onConfirm, workflowType, validationReports, validationContextNote }) => {
  // Blocking issues surfaced by the integration/missing-dependency preview (properties or
  // relations missing on the target). The backend hard-blocks these imports; disable Confirm up front.
  const [hasBlockingIssues, setHasBlockingIssues] = React.useState(false);
  const {
    groupedResults,
    getSelectedItems,
    checklistResults,
    selectedItems,
    toggleItemSelection,
    ontologyRecords,
    selectedOntologyRecords,
    toggleOntologyRecordSelection,
    getSelectedOntologyRecords,
    selectedChecklists,
    selectedServiceId,
    selectedFacilityId,
    targetServiceId,
    targetFacilityId,
    autoIdObjectTypeIds,
    counterModeByObjectType,
    setCounterMode,
    plainImportChecklistIds
  } = useWorkflowStore();

  // In a mixed linked/standard run the plain review covers only the standard subset — preview
  // exactly what this confirm will import. null ⇒ the whole selection.
  const reviewChecklists = plainImportChecklistIds
    ? selectedChecklists.filter((c) => plainImportChecklistIds.includes(c.id))
    : selectedChecklists;

  // Check if we have ontology records
  const hasOntologyRecords = ontologyRecords.length > 0;

  // Check if any items are selected (for ontology migration)
  const hasSelectedItems = hasOntologyRecords
    ? getSelectedOntologyRecords().length > 0
    : getSelectedItems().length > 0;

  // Process migration may proceed only when every OBJECT_TYPE record is MATCH (same id + externalId on
  // the target). CONFLICT (exists under a different id) or MISSING means the target ontology isn't an
  // exact match — migrate/reconcile the ObjectType first.
  const allObjectTypesMatch = ontologyRecords
    .filter(record => record.type === "OBJECT_TYPE")
    .every(record => record.status === "MATCH");

  // Determine if confirm button should be enabled
  // Process migration: enable only when all OBJECT_TYPE records are MATCH (or there are no records)
  // Ontology migration: Enable if any items are selected
  const canConfirm = workflowType === 'process'
    ? ontologyRecords.length === 0 || allObjectTypesMatch
    : hasSelectedItems;

  return (
    <div>
      <h2 className="text-xl font-semibold text-foreground mb-4">
        Checklist Import Results
      </h2>
      <p className="text-muted-foreground mb-6">
        The following items were found in the imported checklist.
      </p>

      {/* Results Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {Object.entries(groupedResults).map(([type, count]) => (
          <div
            key={type}
            className="bg-muted rounded-lg p-4 border border-border"
          >
            <h3 className="font-medium text-foreground mb-1">
              {type.replace("_", " ")}
            </h3>
            <p className="text-2xl font-bold text-primary">
              {count}
            </p>
          </div>
        ))}
      </div>

      {/* Display the appropriate table based on the response format */}
      {hasOntologyRecords ? (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-4">Ontology Records</h3>
          <OntologyRecordsTable
            records={ontologyRecords}
            selectedRecords={selectedOntologyRecords}
            onSelectionChange={toggleOntologyRecordSelection}
            autoIdObjectTypeIds={autoIdObjectTypeIds}
            counterModeByObjectType={counterModeByObjectType}
            onCounterModeChange={setCounterMode}
          />
        </div>
      ) : (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-4">Checklist Items</h3>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Display Name</TableHead>
                  <TableHead>External ID</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Object Type</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {checklistResults.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      No items found.
                    </TableCell>
                  </TableRow>
                ) : (
                  checklistResults.map((item) => {
                    const isSelectable = item.status !== "MATCH";
                    const isSelected = selectedItems[item.id] || false;
                    
                    return (
                      <TableRow 
                        key={item.id}
                        className={cn(
                          !isSelectable && "opacity-60 bg-muted/40",
                          isSelected && "bg-primary/5"
                        )}
                      >
                        <TableCell>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => 
                              toggleItemSelection(item.id, checked === true)
                            }
                            disabled={!isSelectable}
                            aria-label={`Select ${item.displayName}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{item.displayName}</TableCell>
                        <TableCell>{item.externalId}</TableCell>
                        <TableCell>{item.type}</TableCell>
                        <TableCell>
                          <Badge
                            variant={item.status === "MISSING" || item.status === "CONFLICT" || item.status === "SYNC_FAILED" ? "destructive" : "outline"}
                            className={cn(
                              item.status === "MATCH" && "bg-green-100 text-green-800 hover:bg-green-100 border-green-200",
                              item.status === "MISSING" && "bg-red-100 text-red-800 hover:bg-red-100 border-red-200",
                              item.status === "CONFLICT" && "bg-orange-100 text-orange-800 hover:bg-orange-100 border-orange-200",
                              item.status === "SYNC_FAILED" && "bg-amber-100 text-amber-900 hover:bg-amber-100 border-amber-300"
                            )}
                          >
                            {item.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{item.objectTypeName}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Aborted-batch report: per-checklist reasons from server-side validation. */}
      {validationReports && validationReports.length > 0 && (
        <ChecklistValidationReportPanel reports={validationReports} contextNote={validationContextNote} />
      )}

      {/* Integration-parameter preview (process flow, when integration params are included).
          Also surfaces missing properties/relations — blocking issues that disable Confirm. */}
      {workflowType === 'process' && (
        <IntegrationParameterPreview
          checklistIds={reviewChecklists.map((c) => c.id)}
          serviceId={selectedServiceId || ""}
          facilityId={selectedFacilityId || ""}
          targetServiceId={targetServiceId || undefined}
          targetFacilityId={targetFacilityId || undefined}
          onBlockingIssues={setHasBlockingIssues}
        />
      )}

      {/* Confirm and Back Buttons */}
      <div className="mt-8 flex justify-end space-x-4">
        <Button
          onClick={onBack}
          variant="outline"
          className="px-6"
        >
          Back
        </Button>
        <Button
          onClick={onConfirm}
          disabled={!canConfirm || hasBlockingIssues}
          variant="default"
          className="px-6"
        >
          Confirm
        </Button>
      </div>
    </div>
  );
};

export default WorkflowResults;
